import { lazy, memo, Suspense, useCallback, useState } from 'react';
import Groups from '@/pages/groups';
import SearchPage from '@/pages/search';
import NotificationsPage from '@/pages/notifications';
import { InboxSessionProvider } from '@/components/notifications/InboxSessionContext';
import TasksPage from '@/pages/tasks';
import DrawerHeader from '@/components/drawers/DrawerHeader';
import type { DrawerType } from '@/stores/useDrawerStore';
import { cn } from '@/lib/utils';
import { NOTIFICATION_DEFAULT_PAGE_SIZE } from '@/config/constants';

// The AI chat brings streamdown, mermaid, katex, two syntax highlighters and TipTap with it (~1.7 MB of the
// entry bundle before this). Loading it on first open keeps all of that off every page's first render.
const AIChatDrawerPanel = lazy(() => import('@/components/chat/AI/AIChatDrawerPanel'));

type DrawerPanelBodyProps = {
  drawerType: NonNullable<DrawerType>;
  className?: string;
};

/**
 * Shared drawer panel content (header + page body) used by desktop drawers and the mobile sidebar pane.
 */
function DrawerPanelBody({ drawerType, className }: DrawerPanelBodyProps) {
  const [notificationsPageNo, setNotificationsPageNo] = useState(1);
  const notificationsPageSize = NOTIFICATION_DEFAULT_PAGE_SIZE;

  const handleNotificationsPageChange = useCallback((newPage: number) => {
    setNotificationsPageNo(newPage);
  }, []);

  if (drawerType === 'ai-chat') {
    return (
      <Suspense fallback={null}>
        <AIChatDrawerPanel className={className} />
      </Suspense>
    );
  }

  const panelSurfaceClass =
    drawerType === 'notifications'
      ? 'bg-[var(--color-surface-light-sl-1)] dark:bg-gray-900'
      : 'bg-surface-light-sl-1 dark:bg-surface-dark-sl-1';

  return (
    <div
      data-mobile-interactive="true"
      className={cn('flex h-full min-h-0 flex-1 flex-col', panelSurfaceClass, className)}
    >
      {drawerType === 'groups' && (
        <>
          <DrawerHeader
            title="Groups"
            drawerType="groups"
            actions={<Groups.Actions compact={true} />}
          />
          <div className="min-h-0 flex-1 overflow-hidden">
            <Groups />
          </div>
        </>
      )}
      {drawerType === 'search' && (
        <>
          <DrawerHeader title="Search" drawerType="search" />
          <div className="min-h-0 flex-1 overflow-hidden">
            <SearchPage />
          </div>
        </>
      )}
      {drawerType === 'notifications' && (
        <InboxSessionProvider>
          <DrawerHeader
            title="Inbox"
            drawerType="notifications"
            customHeader={
              <NotificationsPage.HeaderContent
                pageNo={notificationsPageNo}
                pageSize={notificationsPageSize}
                onPageChange={handleNotificationsPageChange}
              />
            }
          />
          <div className="min-h-0 flex-1 overflow-hidden">
            <NotificationsPage
              pageNo={notificationsPageNo}
              pageSize={notificationsPageSize}
              onPageChange={handleNotificationsPageChange}
            />
          </div>
        </InboxSessionProvider>
      )}
      {drawerType === 'tasks' && (
        <>
          <DrawerHeader title="Tasks" drawerType="tasks" />
          <div className="min-h-0 flex-1 overflow-hidden">
            <TasksPage />
          </div>
        </>
      )}
    </div>
  );
}

export default memo(DrawerPanelBody);
